import { useNavigate } from 'react-router-dom';
import { AppProps } from '../../App'
import MainWrapper from '../../components/MainWrapper';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainBlock from '../../components/MainBlock';
import { getRandomString, isValidServiceName, useAsyncEffect, useEventListener } from '../../utils/functions';
import { Admin, createItem, getItems } from '../../api/API';
import { freeLimits, proLimits, tables } from '../../utils/constants';
import { AuthModel, ProjectModel, ServiceModel } from '../../utils/interfaces';
import Loading from '../../components/Loading';
import AlertWrapper from '../../components/AlertWrapper';
import Alert from '../../components/Alert';
import { Input, Textarea } from '../../components/Input';

const Console = (props: AppProps) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectModel[]>([]);
  const [showAddProject, setShowAddProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [isAddingProject, setIsAddingProject] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // event handlers
  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.add-project-button') && !target.closest('.add-project-alert')) {
      setShowAddProject(false);
    }
  });

  // load projects
  useAsyncEffect(async () => {
    if (props.user) {
      const data = await getItems(tables.projects, { userId: props.user.id });
      setProjects(data.items?.sort((a: ServiceModel, b: ServiceModel) => a.lastUpdate > b.lastUpdate ? -1 : 1) || []);
      setIsLoading(false);
    }
  }, [props.user]);

  // check if user is reached limit
  const hasReachedLimit = () => {
    return props.user?.plan.name === 'pro' ? 
      projects.length >= proLimits.projects : props.user?.plan.name === 'free' ?
      projects.length >= freeLimits.projects : false;
  }

  // submit and create new project
  const submit = async () => {
    setIsAddingProject(true);
    // create new project
    const newProject: ProjectModel = { id: `pj_${getRandomString(32)}`, name: newProjectName, userId: props.user!.id, description: newProjectDescription, adminIds: [{ userId: props.user!.id, access: 'full_access' }], createdAt: new Date().toISOString(), lastUpdate: new Date().toISOString() };
    await createItem(tables.projects, newProject);
    // create auth
    const { userPoolId, clientId } = await Admin.createUserPool(`${newProject.id}`, 'client', 8, true, false, true, true, t('project.auth.subject.template'), t('project.auth.message.template.code'));
    const newAuth: AuthModel = { id: newProject.id, auth: { userPoolId, clientId, passwordRequirements: { minLength: 8, requireNumber: true, requireSymbol: false, requireUppercase: true, requireLowercase: true }, verificationMessage: { DefaultEmailOption: 'CONFIRM_WITH_CODE', Subject: t('project.auth.subject.template'), Message: t('project.auth.message.template.code'), } } };
    await createItem(tables.auths, newAuth);
    // create api key
    await Admin.createApiKey(newProject.id);
    setProjects(prev => [...prev, newProject].sort((a: ServiceModel, b: ServiceModel) => a.createdAt > b.createdAt ? -1 : 1));
    navigate(`/project/${newProject.id}`);
    setIsAddingProject(false);
  }

  return (
    isLoading ? <main className='flex items-center justify-center flex-1'><Loading solid size={8} /></main>
      : <>
        <MainWrapper {...props} withoutSidebar styles={{ alignItems: 'center' }}>
          <div className='flex-1 flex flex-col justify-center items-start py-12 max-w-6xl w-full'>
            <h1 className="font-bold text-3xl text-start w-full">{t('home.console.title')}</h1>
            <p className='my-8 font-light w-full'>{t('home.console.statement')}</p>
            <button onClick={() => setShowAddProject(true)} className='add-project-button bg-primary text-white py-2 px-16 rounded-full text-sm'>{t('home.console.new-project')}</button>
          </div>
          <div className='mt-8 mb-12 max-w-6xl w-full'>
            <div className='font-bold text-xl'>{t('home.console.projects')}</div>
            <div className={`mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4`}>
              {projects.map(project => (
                <div className='cursor-pointer' onClick={() => navigate(`/project/${project.id}`)}>
                  <MainBlock extraclass='hover:bg-primary/5'>
                    <div className='flex flex-col p-2 overflow-hidden aspect-[2/1]'>
                      <div className='text-lg font-bold whitespace-nowrap overflow-hidden text-ellipsis'>{project.name}</div>
                      <div className='whitespace-nowrap overflow-hidden text-ellipsis text-gray-400 font-light text-sm'>{project.description}</div>
                    </div>
                  </MainBlock>
                </div>
              ))}
            </div>
            {projects.length === 0 && <div className='my-16 flex items-center justify-center text-sm font-light'>{t('home.console.no-project')}</div>}
          </div>
        </MainWrapper>

        {showAddProject && <AlertWrapper>
          <Alert
            extraclass='add-project-alert'
            title={t('home.console.new-project')}
            maxWidth={480}
            onClose={() => setShowAddProject(false)}
            body={<div className='flex flex-col gap-4'>
              <div>
                <label className='text-xs md:text-sm text-black/50 m-1'>{t('general.name')}<span className='text-xs ml-2'>({t('home.console.project.new-project.name-description')})</span></label>
                <Input autoFocus value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} />
                {projects.find(p => p.name === newProjectName) && <p className='text-xs text-red-400 m-1'>{t('home.console.project.new-project.name-exists')}</p>}
              </div>
              <div className='mt-2'>
                <label className='text-xs md:text-sm text-black/50 m-1'>{t('general.description')}<span className='text-xs ml-2'>({t('general.optional')})</span></label>
                <Textarea rows={3} value={newProjectDescription} onChange={(e) => setNewProjectDescription(e.target.value)} />
              </div>
            </div>}
            bottom={<div>
              {hasReachedLimit() && <div className='text-xs m-1 text-red-400'>{t('home.console.project.new-project.limit')}</div>}
              <div className='flex'><button onClick={submit} disabled={hasReachedLimit() || isAddingProject || !isValidServiceName(newProjectName) || !!projects.find(p => p.name === newProjectName)} className='flex items-center justify-center bg-primary disabled:bg-gray-300 text-white px-8 py-2 rounded-lg text-sm'>{isAddingProject ? <Loading /> : t('general.create')}</button></div>
            </div>}
          />
        </AlertWrapper>}
      </>
  )
}

export default Console